import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import css from './style.module.css';
import BackSection from '../Services/BackSection';
import OrgSelector from './OrgSelector';
import AddUsersForm from './AddUsersForm';
import CredentialsResults from './CredentialsResults';
import ExistingUsers from './ExistingUsers';
import { bulkCreateUsers, getOrganizations } from './hqApi';
import { isHqPortal } from '../../lib/hqPortal';

export default function HQUsers() {
    const queryClient = useQueryClient();
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [activeTab, setActiveTab] = useState('add');
    const [results, setResults] = useState(null);
    const [lastSubmittedRows, setLastSubmittedRows] = useState([]);
    const [retryRows, setRetryRows] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    const {
        data: organizations = [],
        isLoading: orgsLoading,
        isError: orgsError,
        error: orgsErrorObj,
        refetch: refetchOrgs,
    } = useQuery({
        queryKey: ['hq-organizations'],
        queryFn: getOrganizations,
    });

    const passwordsOnScreen = !!results?.created?.some((user) => user.password);

    // Warn before a reload / tab close while one-time passwords are displayed.
    useEffect(() => {
        if (!passwordsOnScreen) return;
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [passwordsOnScreen]);

    const bulkMutation = useMutation({
        mutationFn: bulkCreateUsers,
        onSuccess: (data, variables) => {
            setResults(data);
            setRetryRows(null);
            queryClient.invalidateQueries({ queryKey: ['hq-users', variables.orgId] });
        },
        onError: (err) => {
            const status = err?.response?.status;
            if (status === 404) {
                setSubmitError('Organization not found. It may have been removed — refresh and select again.');
            } else if (status === 400) {
                const messages = err?.response?.data?.errors
                    ?.map((e) => e.message)
                    .filter(Boolean);
                setSubmitError(
                    messages?.length
                        ? messages.join(' · ')
                        : err?.response?.data?.message || 'No users could be created. Check the rows and try again.'
                );
            } else if (status === 401) {
                setSubmitError('You are not authorized to perform this action.');
            } else {
                setSubmitError(err?.response?.data?.message || 'Failed to create users. Please try again.');
            }
        },
    });

    // Run an action that would discard displayed passwords only after confirmation.
    const guarded = (action) => {
        if (passwordsOnScreen) {
            setPendingAction(() => action);
        } else {
            action();
        }
    };

    const handleSubmit = (rows) => {
        setSubmitError(null);
        const users = rows.map((row) => ({
            email: row.email.trim(),
            name: row.name.trim(),
            userRole: row.userRole,
            active: row.active !== false,
        }));
        setLastSubmittedRows(users);
        bulkMutation.mutate({ orgId: selectedOrg.orgId, users });
    };

    const handleSelectOrg = (org) => {
        if (org?.id === selectedOrg?.id) return;
        guarded(() => {
            setSelectedOrg(org);
            setResults(null);
            setRetryRows(null);
            setSubmitError(null);
            setActiveTab('add');
        });
    };

    const handleNewBatch = () => {
        guarded(() => {
            setResults(null);
            setRetryRows(null);
            setSubmitError(null);
        });
    };

    const handleRetryFailed = () => {
        const failedEmails = new Set(
            (results?.failed || []).map((row) => row.email?.toLowerCase()).filter(Boolean)
        );
        const rows = lastSubmittedRows.filter((row) =>
            failedEmails.has(row.email?.toLowerCase())
        );
        guarded(() => {
            setRetryRows(rows.length ? rows : null);
            setResults(null);
            setSubmitError(null);
        });
    };

    const confirmPendingAction = () => {
        const action = pendingAction;
        setPendingAction(null);
        if (action) action();
    };

    const notAuthorized = orgsError && orgsErrorObj?.response?.status === 401;

    // HQ tooling is never exposed on customer/tenant domains.
    if (!isHqPortal()) {
        return <Navigate to="/policies" replace />;
    }

    return (
        <section className={css.pageSection}>
            <BackSection pageName="HQ Users" to="/policies" />

            <div className={css.header}>
                <i className={`fas fa-building-user ${css.headerIcon}`} aria-hidden="true"></i>
                <div>
                    <h3 className={css.title}>HQ User Provisioning</h3>
                    <p className={css.subtitle}>
                        Create portal users in any customer organization and share their credentials manually.
                    </p>
                </div>
            </div>

            {orgsLoading ? (
                <div className="text-center py-5">
                    <span className="spinner-border" role="status" aria-hidden="true"></span>
                </div>
            ) : notAuthorized ? (
                <div className="alert alert-danger" role="alert">
                    You are not authorized to view this page.
                </div>
            ) : orgsError ? (
                <div className="alert alert-danger d-flex align-items-center gap-3" role="alert">
                    <span>Failed to load organizations.</span>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => refetchOrgs()}>
                        Retry
                    </button>
                </div>
            ) : (
                <>
                    <OrgSelector
                        organizations={organizations}
                        selected={selectedOrg}
                        onSelect={handleSelectOrg}
                    />

                    {!selectedOrg ? (
                        <div className={css.emptyState}>
                            Select an organization to add users or view its existing ones.
                        </div>
                    ) : (
                        <>
                            <div className={css.tabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'add'}
                                    className={`${css.tab} ${activeTab === 'add' ? css.tabActive : ''}`}
                                    onClick={() => setActiveTab('add')}
                                >
                                    Add users
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'existing'}
                                    className={`${css.tab} ${activeTab === 'existing' ? css.tabActive : ''}`}
                                    onClick={() => setActiveTab('existing')}
                                >
                                    Existing users
                                </button>
                            </div>

                            {/* Both panels stay mounted so switching tabs never discards
                                the one-time passwords shown in the results view. */}
                            <div className={css.card} style={{ display: activeTab === 'add' ? 'block' : 'none' }}>
                                {results ? (
                                    <CredentialsResults
                                        results={results}
                                        org={selectedOrg}
                                        onRetryFailed={handleRetryFailed}
                                        onNewBatch={handleNewBatch}
                                    />
                                ) : (
                                    <AddUsersForm
                                        org={selectedOrg}
                                        initialRows={retryRows}
                                        onSubmit={handleSubmit}
                                        isSubmitting={bulkMutation.isPending}
                                        submitError={submitError}
                                    />
                                )}
                            </div>
                            <div style={{ display: activeTab === 'existing' ? 'block' : 'none' }}>
                                <ExistingUsers org={selectedOrg} />
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Confirm discarding one-time passwords */}
            {pendingAction && (
                <div className={css.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="discard-title">
                    <motion.div
                        className={css.modalCard}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="text-center">
                            <i className="fa-solid fa-triangle-exclamation fa-2x text-warning mb-2" aria-hidden="true"></i>
                            <h5 id="discard-title">Leave the credentials view?</h5>
                            <p className="mb-0">
                                You will lose the displayed passwords — copied everything? They cannot
                                be retrieved later, only reset.
                            </p>
                        </div>
                        <div className="d-flex justify-content-center mt-4 gap-3">
                            <button className="btn btn-secondary" type="button" onClick={() => setPendingAction(null)}>
                                Stay
                            </button>
                            <button className="btn btn-danger" type="button" onClick={confirmPendingAction}>
                                Discard passwords
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </section>
    );
}
